import React, { useState } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";
import EulaEmailOfferModal from "./EulaEmailOfferModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EulaAcceptanceModal = ({ user, onAccepted, onDeclined }) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [showEmailOffer, setShowEmailOffer] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const handleAccept = async () => {
    if (!isChecked) {
      toast.error("Please check the box to confirm your acceptance");
      return;
    }

    try {
      setIsAccepting(true);
      await axios.post(
        `${BACKEND_URL}/api/eula/accept`,
        {},
        { withCredentials: true }
      );
      
      toast.success("Agreement accepted. Welcome to Digital Ink AI!");
      
      // Phase 2: Show email offer modal
      setShowEmailOffer(true);
    } catch (error) {
      console.error("Error accepting EULA:", error);
      toast.error("Failed to accept agreement. Please try again.");
    } finally {
      setIsAccepting(false);
    }
  };

  // Fetch EULA text from backend or use embedded version
  const eulaText = `DIGITAL INK AI™ END USER LICENSE AGREEMENT (EULA)
Version 1.0.0

Accepted by: ${user?.first_name || ''} ${user?.last_name || ''}
Email: ${user?.email || ''}
Acceptance Date: ${new Date().toISOString()}

═══════════════════════════════════════════════════════════

1. ACCEPTANCE OF TERMS

By creating an account, accessing, or using Digital Ink AI™ ("Platform," "Service," "we," "us," or "our"), you ("User," "you," or "your") acknowledge that you have read, understood, and agree to be legally bound by this End User License Agreement ("Agreement").

If you do not agree to these terms, you may not access or use the Platform.

═══════════════════════════════════════════════════════════

2. DESCRIPTION OF THE SERVICE

Digital Ink AI™ is a software-as-a-service (SaaS) platform that provides AI-assisted tools for writing, editing, publishing preparation, content analysis, and related creative workflows.

The Platform may include experimental, beta, or evolving features that are subject to change without notice.

═══════════════════════════════════════════════════════════

3. AI-GENERATED CONTENT DISCLAIMER

You acknowledge and agree that:

• AI-generated outputs may be incomplete, inaccurate, biased, outdated, or unsuitable for specific purposes.
• You are solely responsible for reviewing, editing, verifying, and validating all AI-generated content before use.
• Digital Ink AI™ does not provide legal, medical, financial, or professional advice.
• Reliance on AI-generated output is at your own risk.

Digital Ink AI™ makes no warranties regarding the correctness, originality, or fitness of AI-generated content for any purpose.

═══════════════════════════════════════════════════════════

4. CONTENT OWNERSHIP AND LICENSING

4.1 User Content

You retain full ownership of:
• original manuscripts
• uploaded files
• prompts
• text you create or submit

4.2 AI-Generated Content

Unless otherwise stated:
• You are granted a non-exclusive, perpetual license to use, modify, publish, and distribute AI-generated content produced for you.
• Digital Ink AI™ does not claim ownership of your generated outputs.

4.3 Platform Rights

You grant Digital Ink AI™ a limited license to store, process, and transmit content solely as necessary to operate, maintain, and improve the Platform.

═══════════════════════════════════════════════════════════

5. DATA HANDLING AND PRIVACY

Your use of the Platform is subject to our Privacy Policy.

You acknowledge that:
• Some content may be temporarily or persistently stored to enable platform functionality.
• Metadata and usage analytics may be collected for operational, security, and improvement purposes.
• You may request deletion of your data, subject to technical and legal limitations.

═══════════════════════════════════════════════════════════

6. ACCEPTABLE USE

You agree not to use Digital Ink AI™ to:
• violate any applicable laws or regulations
• infringe intellectual property rights
• generate or distribute illegal, harmful, deceptive, or malicious content
• impersonate individuals or entities
• attempt to reverse engineer, exploit, or disrupt the Platform
• misuse AI tools in ways that could cause harm or liability

We reserve the right to suspend or terminate accounts that violate these terms.

═══════════════════════════════════════════════════════════

7. BETA AND EXPERIMENTAL FEATURES

You acknowledge that Digital Ink AI™ may be offered in Beta or pre-release form.

During Beta:
• Features may be incomplete or unstable
• System downtime or data loss may occur
• Performance and availability are not guaranteed

You agree that use of the Platform during Beta is at your own risk.

═══════════════════════════════════════════════════════════

8. ACCOUNT TERMINATION

Digital Ink AI™ reserves the right to:
• suspend or terminate accounts for violations of this Agreement
• modify or discontinue features at any time

You may terminate your account at any time, subject to data retention policies.

═══════════════════════════════════════════════════════════

9. INTELLECTUAL PROPERTY

All platform software, branding, trademarks, and proprietary systems are the exclusive property of Digital Ink AI™ and its licensors.

Nothing in this Agreement grants you ownership of the Platform or its underlying technology.

═══════════════════════════════════════════════════════════

10. DISCLAIMER OF WARRANTIES

THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE."

DIGITAL INK AI™ DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

═══════════════════════════════════════════════════════════

11. LIMITATION OF LIABILITY

TO THE MAXIMUM EXTENT PERMITTED BY LAW:

DIGITAL INK AI™ SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR EXEMPLARY DAMAGES, INCLUDING LOSS OF DATA, PROFITS, OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR USE OF THE PLATFORM.

TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO DIGITAL INK AI™ IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ZERO IF NO FEES WERE PAID.

═══════════════════════════════════════════════════════════

12. INDEMNIFICATION

You agree to indemnify and hold harmless Digital Ink AI™, its officers, directors, employees, and affiliates from any claims, damages, liabilities, or expenses arising from:
• your use of the Platform
• your content
• your violation of this Agreement

═══════════════════════════════════════════════════════════

13. GOVERNING LAW AND VENUE

This Agreement shall be governed by the laws of the State of Colorado, without regard to conflict-of-law principles.

Any disputes shall be resolved exclusively in the state or federal courts located in Colorado.

═══════════════════════════════════════════════════════════

14. CHANGES TO THIS AGREEMENT

We may update this Agreement from time to time.

Material changes will require renewed acceptance. Continued use of the Platform after updates constitutes acceptance where permitted by law.

═══════════════════════════════════════════════════════════

15. ENTIRE AGREEMENT

This Agreement constitutes the entire understanding between you and Digital Ink AI™ regarding use of the Platform and supersedes all prior agreements or representations.

This Agreement includes applicable Addenda, including but not limited to the Publisher Addendum, Paid Subscription & Billing Addendum, and Data Retention & Deletion Addendum, which are incorporated by reference.

═══════════════════════════════════════════════════════════

16. CONTACT INFORMATION

For questions regarding this Agreement, contact:

Digital Ink AI™
Email: Info@DigitalInkAI.com

═══════════════════════════════════════════════════════════

ACKNOWLEDGMENT

By accepting this Agreement, you confirm that:
• you are legally able to enter into this contract
• you understand its terms
• you agree to be bound by it

═══════════════════════════════════════════════════════════

Digital Ink AI™ is committed to transparency, author empowerment, and responsible AI usage. These terms ensure that both you and our platform operate under clear, mutually understood expectations. Thank you for helping us build the future of intelligent publishing.
`.trim();

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await axios.post(
        `${BACKEND_URL}/api/eula/decline`,
        {},
        { withCredentials: true }
      );
      
      // Call parent handler to logout and redirect
      if (onDeclined) {
        onDeclined();
      }
    } catch (error) {
      console.error("Error declining EULA:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsDeclining(false);
    }
  };

  // Show decline confirmation modal
  if (showDeclineConfirm) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-slate-900 mb-3">
              Decline Agreement?
            </h3>
            <p className="text-sm text-slate-700 mb-6 leading-relaxed">
              If you decline the Digital Ink AI™ End User License Agreement, your account will be removed and you will not be able to use the platform unless you create a new account and accept the agreement.
              <br /><br />
              <span className="font-semibold">This action cannot be undone.</span>
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleDecline}
                disabled={isDeclining}
                className="bg-red-600 hover:bg-red-700 text-white w-full"
              >
                {isDeclining ? "Deleting Account..." : "Yes, Delete My Account"}
              </Button>
              <Button
                onClick={() => setShowDeclineConfirm(false)}
                variant="outline"
                disabled={isDeclining}
                className="w-full"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If email offer should be shown, render that instead
  if (showEmailOffer) {
    return <EulaEmailOfferModal onComplete={onAccepted} />;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            Before You Continue: Please Review and Accept Our Terms
          </h2>
          <p className="text-sm text-slate-600 mt-2">
            Digital Ink AI™ is a professional creative platform powered by advanced AI systems. 
            To continue using your account, you must agree to these terms.
          </p>
        </div>

        {/* Scrollable EULA Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-none bg-white">
            <style>{`
              .eula-document {
                font-family: 'Times New Roman', Times, serif;
                font-size: 12pt;
                line-height: 1.6;
                color: #1a1a1a;
              }
              .eula-document h1 {
                font-size: 16pt;
                font-weight: bold;
                text-align: center;
                margin: 0 0 8pt 0;
                color: #000;
              }
              .eula-document h2 {
                font-size: 13pt;
                font-weight: bold;
                margin: 16pt 0 8pt 0;
                color: #000;
              }
              .eula-document h3 {
                font-size: 12pt;
                font-weight: bold;
                margin: 12pt 0 6pt 0;
                color: #000;
              }
              .eula-document p {
                margin: 0 0 10pt 0;
                text-align: justify;
              }
              .eula-document ul {
                margin: 8pt 0 10pt 20pt;
                padding: 0;
              }
              .eula-document li {
                margin: 4pt 0;
              }
              .eula-document .section-divider {
                border-top: 1px solid #ddd;
                margin: 16pt 0;
              }
              .eula-document .meta-info {
                font-size: 11pt;
                color: #444;
                margin-bottom: 12pt;
              }
              .eula-document .disclaimer {
                font-weight: bold;
                text-transform: uppercase;
              }
            `}</style>
            <div className="eula-document">
              <h1>DIGITAL INK AI™ END USER LICENSE AGREEMENT (EULA)</h1>
              
              <div className="meta-info" style={{textAlign: 'center'}}>
                <p style={{marginBottom: '4pt'}}><strong>Version 1.0.0</strong></p>
                <p style={{marginBottom: '4pt'}}>Accepted by: {user?.first_name || ''} {user?.last_name || ''}</p>
                <p style={{marginBottom: '4pt'}}>Email: {user?.email || ''}</p>
                <p>Acceptance Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>

              <div className="section-divider"></div>

              <h2>1. ACCEPTANCE OF TERMS</h2>
              <p>
                By creating an account, accessing, or using Digital Ink AI™ ("Platform," "Service," "we," "us," or "our"), 
                you ("User," "you," or "your") acknowledge that you have read, understood, and agree to be legally bound by 
                this End User License Agreement ("Agreement").
              </p>
              <p>
                If you do not agree to these terms, you may not access or use the Platform.
              </p>

              <div className="section-divider"></div>

              <h2>2. DESCRIPTION OF THE SERVICE</h2>
              <p>
                Digital Ink AI™ is a software-as-a-service (SaaS) platform that provides AI-assisted tools for writing, 
                editing, publishing preparation, content analysis, and related creative workflows.
              </p>
              <p>
                The Platform may include experimental, beta, or evolving features that are subject to change without notice.
              </p>

              <div className="section-divider"></div>

              <h2>3. AI-GENERATED CONTENT DISCLAIMER</h2>
              <p>You acknowledge and agree that:</p>
              <ul>
                <li>AI-generated outputs may be incomplete, inaccurate, biased, outdated, or unsuitable for specific purposes.</li>
                <li>You are solely responsible for reviewing, editing, verifying, and validating all AI-generated content before use.</li>
                <li>Digital Ink AI™ does not provide legal, medical, financial, or professional advice.</li>
                <li>Reliance on AI-generated output is at your own risk.</li>
              </ul>
              <p>
                Digital Ink AI™ makes no warranties regarding the correctness, originality, or fitness of AI-generated content 
                for any purpose.
              </p>

              <div className="section-divider"></div>

              <h2>4. CONTENT OWNERSHIP AND LICENSING</h2>
              
              <h3>4.1 User Content</h3>
              <p>You retain full ownership of:</p>
              <ul>
                <li>original manuscripts</li>
                <li>uploaded files</li>
                <li>prompts</li>
                <li>text you create or submit</li>
              </ul>

              <h3>4.2 AI-Generated Content</h3>
              <p>Unless otherwise stated:</p>
              <ul>
                <li>You are granted a non-exclusive, perpetual license to use, modify, publish, and distribute AI-generated content produced for you.</li>
                <li>Digital Ink AI™ does not claim ownership of your generated outputs.</li>
              </ul>

              <h3>4.3 Platform Rights</h3>
              <p>
                You grant Digital Ink AI™ a limited license to store, process, and transmit content solely as necessary to 
                operate, maintain, and improve the Platform.
              </p>

              <div className="section-divider"></div>

              <h2>5. DATA HANDLING AND PRIVACY</h2>
              <p>Your use of the Platform is subject to our Privacy Policy.</p>
              <p>You acknowledge that:</p>
              <ul>
                <li>Some content may be temporarily or persistently stored to enable platform functionality.</li>
                <li>Metadata and usage analytics may be collected for operational, security, and improvement purposes.</li>
                <li>You may request deletion of your data, subject to technical and legal limitations.</li>
              </ul>

              <div className="section-divider"></div>

              <h2>6. ACCEPTABLE USE</h2>
              <p>You agree not to use Digital Ink AI™ to:</p>
              <ul>
                <li>violate any applicable laws or regulations</li>
                <li>infringe intellectual property rights</li>
                <li>generate or distribute illegal, harmful, deceptive, or malicious content</li>
                <li>impersonate individuals or entities</li>
                <li>attempt to reverse engineer, exploit, or disrupt the Platform</li>
                <li>misuse AI tools in ways that could cause harm or liability</li>
              </ul>
              <p>We reserve the right to suspend or terminate accounts that violate these terms.</p>

              <div className="section-divider"></div>

              <h2>7. BETA AND EXPERIMENTAL FEATURES</h2>
              <p>You acknowledge that Digital Ink AI™ may be offered in Beta or pre-release form.</p>
              <p>During Beta:</p>
              <ul>
                <li>Features may be incomplete or unstable</li>
                <li>System downtime or data loss may occur</li>
                <li>Performance and availability are not guaranteed</li>
              </ul>
              <p>You agree that use of the Platform during Beta is at your own risk.</p>

              <div className="section-divider"></div>

              <h2>8. ACCOUNT TERMINATION</h2>
              <p>Digital Ink AI™ reserves the right to:</p>
              <ul>
                <li>suspend or terminate accounts for violations of this Agreement</li>
                <li>modify or discontinue features at any time</li>
              </ul>
              <p>You may terminate your account at any time, subject to data retention policies.</p>

              <div className="section-divider"></div>

              <h2>9. INTELLECTUAL PROPERTY</h2>
              <p>
                All platform software, branding, trademarks, and proprietary systems are the exclusive property of 
                Digital Ink AI™ and its licensors.
              </p>
              <p>Nothing in this Agreement grants you ownership of the Platform or its underlying technology.</p>

              <div className="section-divider"></div>

              <h2>10. DISCLAIMER OF WARRANTIES</h2>
              <p className="disclaimer">
                The Platform is provided "as is" and "as available."
              </p>
              <p className="disclaimer">
                Digital Ink AI™ disclaims all warranties, express or implied, including but not limited to merchantability, 
                fitness for a particular purpose, and non-infringement.
              </p>

              <div className="section-divider"></div>

              <h2>11. LIMITATION OF LIABILITY</h2>
              <p className="disclaimer">To the maximum extent permitted by law:</p>
              <p className="disclaimer">
                Digital Ink AI™ shall not be liable for any indirect, incidental, consequential, special, or exemplary damages, 
                including loss of data, profits, or business opportunities, arising from your use of the Platform.
              </p>
              <p className="disclaimer">
                Total liability shall not exceed the amount paid by you to Digital Ink AI™ in the twelve (12) months preceding 
                the claim, or zero if no fees were paid.
              </p>

              <div className="section-divider"></div>

              <h2>12. INDEMNIFICATION</h2>
              <p>
                You agree to indemnify and hold harmless Digital Ink AI™, its officers, directors, employees, and affiliates 
                from any claims, damages, liabilities, or expenses arising from:
              </p>
              <ul>
                <li>your use of the Platform</li>
                <li>your content</li>
                <li>your violation of this Agreement</li>
              </ul>

              <div className="section-divider"></div>

              <h2>13. GOVERNING LAW AND VENUE</h2>
              <p>
                This Agreement shall be governed by the laws of the State of Colorado, without regard to conflict-of-law principles.
              </p>
              <p>
                Any disputes shall be resolved exclusively in the state or federal courts located in Colorado.
              </p>

              <div className="section-divider"></div>

              <h2>14. CHANGES TO THIS AGREEMENT</h2>
              <p>We may update this Agreement from time to time.</p>
              <p>
                Material changes will require renewed acceptance. Continued use of the Platform after updates constitutes 
                acceptance where permitted by law.
              </p>

              <div className="section-divider"></div>

              <h2>15. ENTIRE AGREEMENT</h2>
              <p>
                This Agreement constitutes the entire understanding between you and Digital Ink AI™ regarding use of the 
                Platform and supersedes all prior agreements or representations.
              </p>
              <p>
                This Agreement includes applicable Addenda, including but not limited to the Publisher Addendum, Paid 
                Subscription & Billing Addendum, and Data Retention & Deletion Addendum, which are incorporated by reference.
              </p>

              <div className="section-divider"></div>

              <h2>16. CONTACT INFORMATION</h2>
              <p>For questions regarding this Agreement, contact:</p>
              <p style={{marginLeft: '20pt'}}>
                <strong>Digital Ink AI™</strong><br />
                Email: Info@DigitalInkAI.com
              </p>

              <div className="section-divider"></div>

              <h2>ACKNOWLEDGMENT</h2>
              <p>By accepting this Agreement, you confirm that:</p>
              <ul>
                <li>you are legally able to enter into this contract</li>
                <li>you understand its terms</li>
                <li>you agree to be bound by it</li>
              </ul>

              <div className="section-divider"></div>

              <p style={{textAlign: 'center', fontStyle: 'italic', marginTop: '16pt'}}>
                Digital Ink AI™ is committed to transparency, author empowerment, and responsible AI usage. 
                These terms ensure that both you and our platform operate under clear, mutually understood expectations. 
                Thank you for helping us build the future of intelligent publishing.
              </p>
            </div>
          </div>
        </div>

        {/* Footer with Checkbox and Button */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="flex items-start space-x-3 mb-4">
            <input
              type="checkbox"
              id="eula-accept"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
            />
            <label htmlFor="eula-accept" className="text-sm text-slate-700">
              I have read and agree to the{" "}
              <span className="font-semibold">Digital Ink AI™ Terms of Use</span> and{" "}
              <span className="font-semibold">Privacy Policy</span>.
            </label>
          </div>

          <div className="flex justify-between gap-3">
            <Button
              onClick={() => setShowDeclineConfirm(true)}
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 px-6"
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!isChecked || isAccepting}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAccepting ? "Processing..." : "Accept & Continue"}
            </Button>
          </div>

          <p className="text-xs text-slate-500 mt-3 text-center">
            By accepting, you acknowledge that you understand these terms and agree to be legally bound by them.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EulaAcceptanceModal;
